import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import { DatePicker } from '../../components/ui/date-picker';
import { 
  Loader2, 
  Package,
  User,
  Boxes,
  Eye,
  Edit,
  AlertTriangle,
  Lock,
  Trash2,
  Plus,
  CheckCircle2,
  Barcode,
  MapPin,
  Phone,
  FileText,
} from 'lucide-react';
import { getStatusInfo } from './constants';
import { getCustomerAddresses } from '../../lib/api';
import { PoPartsTree } from '../../components/phased/PoPartsTree';
import { Split } from 'lucide-react';

/**
 * View/Edit Order Dialog for Order Pipeline
 */
export default function ViewOrderDialog({
  open,
  onOpenChange,
  selectedOrder,
  editingOrder,
  setEditingOrder,
  orderFormData,
  setOrderFormData,
  customers,
  products,
  cobrandings,
  canEdit,
  canEditOrderFully,
  canEditOrderHeader,
  handleSaveOrder,
  savingOrder,
  userRole,
  onSplitFulfilment,
  onOpenPart,
  onOpenPoSummary,
}) {
  const navigate = useNavigate();
  const addOrderLine = () => {
    setOrderFormData({
      ...orderFormData,
      lines: [...orderFormData.lines, {
        product_id: '', quantity: '', cases: '', estimated_weight: '',
        notes: '', cobranding_id: '', supplier_product_code: '', use_by_date: ''
      }]
    });
  };

  const updateOrderLine = (index, field, value) => {
    setOrderFormData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = { ...newLines[index], [field]: value };
      return { ...prev, lines: newLines };
    });
  };

  const removeOrderLine = (index) => {
    if (orderFormData.lines.length > 1) {
      setOrderFormData({
        ...orderFormData,
        lines: orderFormData.lines.filter((_, i) => i !== index)
      });
    }
  };

  // Get customer data for delivery address display
  const customer = customers.find(c => c.id === (selectedOrder?.customer_id || orderFormData?.customer_id));

  // ── Delivery address loading & helpers ───────────────────────────────
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  const buildPrimaryAddrFromCustomer = (c) => {
    if (!c) return null;
    if (!c.delivery_address_line1 && !c.delivery_city) return null;
    return {
      id: 'primary',
      label: 'Primary Address',
      address_line1: c.delivery_address_line1 || null,
      address_line2: c.delivery_address_line2 || null,
      city: c.delivery_city || null,
      county: c.delivery_county || null,
      postcode: c.delivery_postcode || null,
      country: c.delivery_country || 'Ireland',
      contact_name: c.contact_name || null,
      contact_phone: c.contact_phone || null,
      delivery_instructions: c.default_delivery_instructions || null,
      is_primary: true,
    };
  };

  // The address that was actually saved on the dispatch
  const savedSelectedAddr = selectedOrder?.selected_delivery_address || null;
  const customerPrimaryAddr = useMemo(() => buildPrimaryAddrFromCustomer(customer), [customer]);

  // Effective address shown in view mode (or as initial in edit mode)
  const effectiveSelectedAddr = savedSelectedAddr || customerPrimaryAddr;
  const isUsingPrimary = !savedSelectedAddr || savedSelectedAddr?.is_primary || savedSelectedAddr?.id === 'primary';

  const formatAddressLines = (a) => {
    if (!a) return [];
    const ok = (v) => v && String(v).trim().toLowerCase() !== 'nan';
    const cityPost = [a.city, a.postcode].filter(ok).join(', ');
    return [a.address_line1, a.address_line2, cityPost, a.county, a.country].filter(ok);
  };
  const contactPhone = [selectedOrder?.delivery_contact_phone, effectiveSelectedAddr?.contact_phone]
    .find(v => v && String(v).trim().toLowerCase() !== 'nan');

  // Load customer addresses when entering edit mode (so the dropdown is populated)
  useEffect(() => {
    if (!editingOrder || !customer?.id) return;
    let mounted = true;
    setLoadingAddresses(true);
    getCustomerAddresses(customer.id)
      .then(res => {
        if (!mounted) return;
        const all = res.data?.all_addresses || [];
        setCustomerAddresses(all);
      })
      .catch(() => {
        if (mounted) setCustomerAddresses(customerPrimaryAddr ? [customerPrimaryAddr] : []);
      })
      .finally(() => mounted && setLoadingAddresses(false));
    return () => { mounted = false; };
  }, [editingOrder, customer?.id, customerPrimaryAddr]);

  const handleAddressChange = (addrId) => {
    const all = customerAddresses.length ? customerAddresses : (customerPrimaryAddr ? [customerPrimaryAddr] : []);
    const picked = all.find(a => a.id === addrId);
    if (!picked) return;
    const addrText = formatAddressLines(picked).join(', ');
    setOrderFormData(prev => ({
      ...prev,
      delivery_address_id: picked.id,
      delivery_address: addrText,
      selected_delivery_address: {
        id: picked.id,
        label: picked.label,
        address_line1: picked.address_line1 || null,
        address_line2: picked.address_line2 || null,
        city: picked.city || null,
        county: picked.county || null,
        postcode: picked.postcode || null,
        country: picked.country || null,
        contact_name: picked.contact_name || null,
        contact_phone: picked.contact_phone || null,
        delivery_instructions: picked.delivery_instructions || null,
        is_primary: !!picked.is_primary || picked.id === 'primary',
      },
      delivery_contact_name: picked.contact_name || null,
      delivery_contact_phone: picked.contact_phone || null,
      delivery_instructions: picked.delivery_instructions || null,
    }));
  };

  // The address currently reflected in the form (updated by edit dropdown)
  const formSelectedAddr = orderFormData?.selected_delivery_address || effectiveSelectedAddr;
  const formIsUsingPrimary = formSelectedAddr?.is_primary || formSelectedAddr?.id === 'primary';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {editingOrder ? <Edit className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            {editingOrder ? 'Edit Order' : 'Order Details'}
            {selectedOrder && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                #{selectedOrder.dispatch_number}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {selectedOrder && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(selectedOrder.status).color}`}>
                {getStatusInfo(selectedOrder.status).label}
              </span>
            )}
            {!canEditOrderFully(selectedOrder) && selectedOrder?.status !== 'draft' && (
              <span className="ml-2 text-xs text-amber-600">
                (Product lines locked - batches created)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        {selectedOrder && (
          <div className="space-y-4 py-2">
            {/* Strong warning for dispatched orders — view-only mode */}
            {selectedOrder?.status === 'dispatched' && (
              <Alert className="border-red-200 bg-red-50" data-testid="dispatched-order-warning">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-800">View Only — Order Already Dispatched</AlertTitle>
                <AlertDescription className="text-red-700">
                  This order has already been dispatched and cannot be edited directly because
                  it may affect dispatch records, traceability, customer documentation, and audit history.
                </AlertDescription>
              </Alert>
            )}

            {/* Status indicator for locked lines */}
            {!canEditOrderFully(selectedOrder) && editingOrder && selectedOrder?.status !== 'dispatched' && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Limited Editing</AlertTitle>
                <AlertDescription className="text-amber-700">
                  Product lines are locked because batches have been created. 
                  You can still edit customer, PO number, dates, and notes.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Customer */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Customer
              </Label>
              {editingOrder && canEditOrderHeader(selectedOrder) ? (
                <Select 
                  value={orderFormData.customer_id} 
                  onValueChange={(v) => setOrderFormData({ ...orderFormData, customer_id: v })}
                >
                  <SelectTrigger data-testid="edit-customer-select">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm font-medium py-2 px-3 bg-muted/30 rounded">
                  {selectedOrder.customer_name}
                </div>
              )}
            </div>

            {/* Delivery Address — what was actually selected at order entry */}
            {(effectiveSelectedAddr || editingOrder) && (
              <div className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
                  <Label className="flex items-center gap-2 m-0">
                    <MapPin className="w-4 h-4" /> Deliver To
                  </Label>
                  {!editingOrder && (
                    isUsingPrimary
                      ? <Badge variant="outline" className="text-[10px]">Customer's Primary Address</Badge>
                      : <Badge variant="outline" className="text-[10px] bg-indigo-50 border-indigo-200 text-indigo-800">Alternate Destination</Badge>
                  )}
                </div>
                <div className="p-3 space-y-2">
                  {editingOrder && canEditOrderHeader(selectedOrder) ? (
                    <>
                      <Select
                        value={formSelectedAddr?.id || ''}
                        onValueChange={handleAddressChange}
                        disabled={loadingAddresses}
                      >
                        <SelectTrigger data-testid="edit-delivery-address-select">
                          <SelectValue placeholder={loadingAddresses ? 'Loading addresses…' : 'Pick delivery destination…'} />
                        </SelectTrigger>
                        <SelectContent>
                          {(customerAddresses.length ? customerAddresses : (customerPrimaryAddr ? [customerPrimaryAddr] : [])).map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              {a.label || (a.is_primary ? 'Primary' : a.address_line1) || a.id}
                              {(a.is_primary || a.id === 'primary') ? ' (primary)' : ''}
                              {a.city ? ` — ${a.city}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!formIsUsingPrimary && formSelectedAddr && (
                        <div className="text-xs text-muted-foreground bg-indigo-50 border border-indigo-100 rounded px-2 py-1.5">
                          {formatAddressLines(formSelectedAddr).map((l, i) => <div key={i}>{l}</div>)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm">
                      {effectiveSelectedAddr?.label && !isUsingPrimary && (
                        <div className="font-semibold text-foreground mb-0.5">{effectiveSelectedAddr.label}</div>
                      )}
                      {formatAddressLines(effectiveSelectedAddr).map((l, i) => (
                        <div key={i} className="text-foreground/90">{l}</div>
                      ))}
                      {!effectiveSelectedAddr && (
                        <div className="text-muted-foreground italic">No address recorded.</div>
                      )}
                    </div>
                  )}

                  {/* Contact + instructions on the dispatch (or selected address) */}
                  {(selectedOrder.delivery_contact_name || selectedOrder.delivery_contact_phone || effectiveSelectedAddr?.contact_name || effectiveSelectedAddr?.contact_phone) && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1.5 border-t">
                      <Phone className="w-3 h-3" />
                      <span>
                        {selectedOrder.delivery_contact_name || effectiveSelectedAddr?.contact_name || '—'}
                        {contactPhone && (
                          <span className="ml-1 font-mono">({contactPhone})</span>
                        )}
                      </span>
                    </div>
                  )}
                  {(selectedOrder.delivery_instructions || effectiveSelectedAddr?.delivery_instructions) && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground pt-1.5 border-t">
                      <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span className="whitespace-pre-wrap">
                        {selectedOrder.delivery_instructions || effectiveSelectedAddr?.delivery_instructions}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* PO Number, PO Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>PO Number</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <Input
                    value={orderFormData.po_number}
                    onChange={(e) => setOrderFormData({ ...orderFormData, po_number: e.target.value })}
                    placeholder="PO number..."
                    className="font-mono"
                    data-testid="edit-po-number"
                  />
                ) : (
                  <div className="text-sm font-mono py-2 px-3 bg-muted/30 rounded">
                    {selectedOrder.po_number || '-'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>PO Date</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <DatePicker
                    value={orderFormData.po_date}
                    onChange={(v) => setOrderFormData({ ...orderFormData, po_date: v })}
                    placeholder="Select date"
                  />
                ) : (
                  <div className="text-sm font-mono py-2 px-3 bg-muted/30 rounded">
                    {selectedOrder.po_date || '-'}
                  </div>
                )}
              </div>
            </div>

            {selectedOrder.po_group_id && (
              <PoPartsTree order={selectedOrder} onOpenPart={onOpenPart} onOpenSummary={onOpenPoSummary} />
            )}
            
            {/* Dispatch Date + Transport Agent */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dispatch Date</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <DatePicker
                    value={orderFormData.dispatch_date}
                    onChange={(v) => setOrderFormData({ ...orderFormData, dispatch_date: v })}
                    placeholder="Select date"
                  />
                ) : (
                  <div className="text-sm font-mono py-2 px-3 bg-muted/30 rounded" data-testid="order-dispatch-date">
                    {selectedOrder.dispatch_date || '-'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Transport Agent</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <Input
                    value={orderFormData.vehicle_info}
                    onChange={(e) => setOrderFormData({ ...orderFormData, vehicle_info: e.target.value })}
                    placeholder="Transport agent..."
                    data-testid="edit-transport"
                  />
                ) : (
                  <div className="text-sm py-2 px-3 bg-muted/30 rounded" data-testid="order-transport-agent">
                    {selectedOrder.vehicle_info || '-'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Product Lines */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Product Lines
                {!canEditOrderFully(selectedOrder) && (
                  <Lock className="w-3 h-3 text-muted-foreground" />
                )}
              </Label>
              
              {canEditOrderFully(selectedOrder) && editingOrder ? (
                /* ===== EDIT MODE ===== */
                <div className="space-y-2">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_90px_1fr_36px] gap-2 px-3 py-2 bg-muted/50 rounded-t-lg text-xs font-medium text-muted-foreground">
                    <div>Product</div>
                    <div>Customer Code</div>
                    <div>Brand/Pkg</div>
                    <div>Units</div>
                    <div>Cases</div>
                    <div>Wt (kg)</div>
                    <div>Use By</div>
                    <div>Notes</div>
                    <div></div>
                  </div>
                  {orderFormData.lines.map((line, index) => (
                    <div key={index} className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_90px_1fr_36px] gap-2 items-end p-2 bg-muted/20 rounded-lg">
                      <div>
                        <Select value={line.product_id} onValueChange={(v) => {
                          const product = products.find(p => p.id === v);
                          updateOrderLine(index, 'product_id', v);
                          if (product) updateOrderLine(index, 'product_name', product.name);
                        }}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`edit-line-product-${index}`}>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Input
                          value={line.supplier_product_code || ''}
                          onChange={(e) => updateOrderLine(index, 'supplier_product_code', e.target.value)}
                          placeholder="Code..."
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-supplier-code-${index}`}
                        />
                      </div>
                      <div>
                        <Select 
                          value={line.cobranding_id || 'standard'} 
                          onValueChange={(v) => updateOrderLine(index, 'cobranding_id', v === 'standard' ? '' : v)}
                        >
                          <SelectTrigger className="h-9 text-xs" data-testid={`edit-line-cobranding-${index}`}>
                            <SelectValue placeholder="Standard" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard</SelectItem>
                            {cobrandings.map(cb => (
                              <SelectItem key={cb.id} value={cb.id}>{cb.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={line.quantity || ''}
                          onChange={(e) => updateOrderLine(index, 'quantity', parseFloat(e.target.value) || '')}
                          placeholder="0"
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-qty-${index}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={line.cases || ''}
                          onChange={(e) => updateOrderLine(index, 'cases', e.target.value)}
                          placeholder="0"
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-cases-${index}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          step="0.01"
                          value={line.estimated_weight || ''}
                          onChange={(e) => updateOrderLine(index, 'estimated_weight', e.target.value)}
                          placeholder="0.00"
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-weight-${index}`}
                        />
                      </div>
                      <div>
                        {line.use_by_date === 'No Date' ? (
                          <div className="h-9 flex items-center px-2 bg-amber-50 border border-amber-200 rounded-md">
                            <span className="text-[10px] font-medium text-amber-700">No Date</span>
                          </div>
                        ) : (
                          <DatePicker
                            value={line.use_by_date || ''}
                            onChange={(v) => updateOrderLine(index, 'use_by_date', v)}
                            placeholder="Date"
                            data-testid={`edit-line-usebydate-${index}`}
                          />
                        )}
                      </div>
                      <div>
                        <Input
                          value={line.notes || ''}
                          onChange={(e) => updateOrderLine(index, 'notes', e.target.value)}
                          placeholder="Notes..."
                          className="text-xs h-9"
                          data-testid={`edit-line-notes-${index}`}
                        />
                      </div>
                      <div className="flex justify-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeOrderLine(index)}
                          disabled={orderFormData.lines.length === 1}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addOrderLine} data-testid="edit-add-line">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Product Line
                  </Button>
                </div>
              ) : (
                /* ===== VIEW MODE ===== */
                <div className="space-y-1">
                  {/* View header */}
                  <div className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_80px_70px_1fr] gap-2 px-3 py-2 bg-muted/50 rounded-t-lg text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    <div>Product</div>
                    <div>Customer Code</div>
                    <div>Brand/Pkg</div>
                    <div>Units</div>
                    <div>Cases</div>
                    <div>Wt (kg)</div>
                    <div>Use By</div>
                    <div>Batch</div>
                    <div>Notes</div>
                  </div>
                  {selectedOrder.lines?.map((line, index) => {
                    const cobranding = cobrandings.find(c => c.id === line.cobranding_id);
                    const weight = line.total_weight || line.weight_override || line.estimated_weight || 0;
                    const cases = line.cases_override || line.calculated_cases || line.cases || 0;
                    const useByDate = line.use_by_date || '';
                    return (
                      <div key={index} className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_80px_70px_1fr] gap-2 p-2.5 bg-muted/20 rounded-lg text-sm items-center">
                        <div className="font-medium text-xs truncate" title={line.product_name}>
                          {line.product_name}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground truncate" title={line.supplier_product_code}>
                          {line.supplier_product_code || '-'}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {cobranding?.name || 'Standard'}
                        </div>
                        <div className="font-mono text-xs">
                          {line.quantity}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {cases ? parseFloat(cases).toFixed(0) : '-'}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {weight ? `${parseFloat(weight).toFixed(2)}` : '-'}
                        </div>
                        <div className={`font-mono text-xs ${useByDate === 'No Date' ? 'text-amber-600 italic' : 'text-muted-foreground'}`}>
                          {useByDate === 'No Date' ? 'No Date' : (useByDate || '-')}
                        </div>
                        <div>
                          {line.batch_code ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 font-mono">
                              <Boxes className="w-3 h-3" />
                              {line.batch_code}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate" title={line.notes}>
                          {line.notes || '-'}
                        </div>
                      </div>
                    );
                  }) || (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No product lines
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              {editingOrder && canEditOrderHeader(selectedOrder) ? (
                <Textarea
                  value={orderFormData.notes}
                  onChange={(e) => setOrderFormData({ ...orderFormData, notes: e.target.value })}
                  placeholder="Order notes..."
                  rows={2}
                  data-testid="edit-order-notes"
                />
              ) : (
                <div className="text-sm py-2 px-3 bg-muted/30 rounded min-h-[40px]">
                  {selectedOrder.notes || '-'}
                </div>
              )}
            </div>
          </div>
        )}
        
        <DialogFooter>
          {!editingOrder ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {canEdit(userRole) && selectedOrder?.can_split_fulfilment && onSplitFulfilment && (
                <Button variant="outline" onClick={() => onSplitFulfilment(selectedOrder)} className="border-primary/40 text-primary" data-testid="btn-view-split-fulfilment">
                  <Split className="w-4 h-4 mr-2" />
                  Split Fulfilment
                </Button>
              )}
              {canEdit(userRole) && canEditOrderHeader(selectedOrder) && (
                <Button onClick={() => { onOpenChange(false); navigate(`/sales-order/${selectedOrder.id}`); }} data-testid="btn-edit-order">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Order
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditingOrder(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveOrder} disabled={savingOrder} data-testid="btn-save-order">
                {savingOrder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
